import { useCallback, useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import {
  fetchSurveyTemplates,
  createSurveyTemplate,
  archiveSurveyTemplate,
  updateSurveyTemplate,
  fetchCompanies,
} from "../api/adminClient";
import { LENS_ROUTES } from "./routes";

function TemplateCard({ tpl, onOpen, onArchive, onRestore, onRename }) {
  const [renaming, setRenaming] = useState(false);
  const [renameVal, setRenameVal] = useState(tpl.name);
  const [savingRename, setSavingRename] = useState(false);

  const handleRenameConfirm = async () => {
    if (!renameVal.trim() || renameVal.trim() === tpl.name) {
      setRenaming(false);
      return;
    }
    setSavingRename(true);
    try {
      await onRename(tpl.id, renameVal.trim());
      setRenaming(false);
    } finally {
      setSavingRename(false);
    }
  };

  return (
    <div
      className={`flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-4 rounded-xl border transition-all ${
        tpl.is_archived
          ? "bg-gray-50 border-gray-200 opacity-60"
          : "bg-white border-slate-200 shadow-sm hover:shadow"
      }`}
    >
      {/* Name + counts */}
      <div className="flex-1 min-w-0">
        {renaming ? (
          <div className="flex items-center gap-2">
            <input
              autoFocus
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-48"
              value={renameVal}
              onChange={(e) => setRenameVal(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleRenameConfirm();
                if (e.key === "Escape") setRenaming(false);
              }}
            />
            <button
              type="button"
              onClick={handleRenameConfirm}
              disabled={savingRename}
              className="px-3 py-1.5 text-xs rounded-full font-medium text-white bg-[#004F73] hover:opacity-90 disabled:opacity-50"
            >
              {savingRename ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              onClick={() => setRenaming(false)}
              className="px-3 py-1.5 text-xs rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setRenaming(true)}
            className="text-left group"
            title="Click to rename"
          >
            <p className="font-semibold text-gray-900 group-hover:text-[#004F73] transition-colors">
              {tpl.name}
              <span className="ml-1.5 text-xs text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity">
                ✎
              </span>
            </p>
          </button>
        )}

        <div className="flex items-center gap-3 mt-1">
          <span className="text-xs text-gray-500">
            {tpl.question_count} question{tpl.question_count !== 1 ? "s" : ""}
          </span>
          <span className="text-gray-300">·</span>
          <span className="text-xs text-gray-500">
            {tpl.lite_count} in LITE
          </span>
          {tpl.is_archived && (
            <>
              <span className="text-gray-300">·</span>
              <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                Archived
              </span>
            </>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 shrink-0">
        {!tpl.is_archived && (
          <button
            type="button"
            onClick={() => onOpen(tpl)}
            className="px-4 py-2 text-sm font-semibold rounded-full border border-[#004F73] text-[#004F73] hover:bg-[#004F73]/5"
          >
            Open
          </button>
        )}
        {tpl.is_archived ? (
          <button
            type="button"
            onClick={() => onRestore(tpl.id)}
            className="px-4 py-2 text-sm font-semibold rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Restore
          </button>
        ) : (
          <button
            type="button"
            onClick={() => onArchive(tpl.id)}
            className="px-4 py-2 text-sm font-semibold rounded-full border border-red-200 text-red-500 hover:bg-red-50"
          >
            Archive
          </button>
        )}
      </div>
    </div>
  );
}

function CreateTemplateModal({ open, onClose, onConfirm, saving, templates }) {
  const [name, setName] = useState("");
  const [cloneFrom, setCloneFrom] = useState("");

  useEffect(() => {
    if (open) {
      setName("");
      setCloneFrom("");
    }
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-600/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
    >
      <div className="relative p-4 w-full max-w-md">
        <div className="bg-white rounded-xl shadow-lg">
          <div className="flex items-center justify-between px-5 py-4 border-b">
            <h3 className="text-lg font-semibold text-gray-900">New Template</h3>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl leading-none w-8 h-8 flex items-center justify-center"
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div className="px-5 py-4 space-y-4">
            <div>
              <label className="block text-sm font-bold text-[#004F73] mb-1">
                Template name <span className="text-red-500">*</span>
              </label>
              <input
                autoFocus
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="bg-gray-50 border border-gray-300 text-sm rounded-lg w-full p-2.5"
                placeholder="e.g. Q3 2026 Assessment"
              />
            </div>

            {templates.length > 0 && (
              <div>
                <label className="block text-sm font-bold text-[#004F73] mb-1">
                  Clone questions from (optional)
                </label>
                <select
                  value={cloneFrom}
                  onChange={(e) => setCloneFrom(e.target.value)}
                  className="bg-gray-50 border border-gray-300 text-sm rounded-lg w-full p-2.5"
                >
                  <option value="">— Start blank —</option>
                  {templates
                    .filter((t) => !t.is_archived)
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({t.question_count} questions)
                      </option>
                    ))}
                </select>
              </div>
            )}

            <p className="text-xs text-gray-400">
              The template name is only visible to admins — participants never see it.
            </p>
          </div>
          <div className="flex items-center justify-end gap-3 px-5 py-4 border-t">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-sm rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onConfirm({ name, base_template_id: cloneFrom || undefined })}
              disabled={saving || !name.trim()}
              className="px-4 py-2 text-sm rounded-full font-medium text-white bg-[#004F73] hover:opacity-90 disabled:opacity-50"
            >
              {saving ? "Creating…" : "Create Template"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SurveyTemplatesPage({ embedded = false }) {
  const { setAdminAssistantContext } = useOutletContext() || {};
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [scopeOrgId, setScopeOrgId] = useState("");
  const [companies, setCompanies] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchSurveyTemplates(scopeOrgId);
      setTemplates(res.data?.templates || []);
    } catch (e) {
      toast.error(e.message || "Could not load templates");
    } finally {
      setLoading(false);
    }
  }, [scopeOrgId]);

  useEffect(() => {
    fetchCompanies()
      .then((res) => setCompanies(res.data?.companies || []))
      .catch(() => setCompanies([]));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    return () => setAdminAssistantContext?.(null);
  }, [setAdminAssistantContext]);

  const handleCreate = async ({ name, base_template_id }) => {
    setCreating(true);
    try {
      await createSurveyTemplate({
        name,
        base_template_id,
        ...(scopeOrgId ? { organization_id: scopeOrgId } : {}),
      });
      toast.success("Template created");
      setCreateOpen(false);
      await load();
    } catch (e) {
      toast.error(e.message || "Could not create template");
    } finally {
      setCreating(false);
    }
  };

  const handleOpen = (tpl) => {
    const orgQ = scopeOrgId
      ? `&organization_id=${encodeURIComponent(scopeOrgId)}`
      : "";
    navigate(
      `${LENS_ROUTES.studioSurvey}&template_id=${encodeURIComponent(tpl.id)}${orgQ}`
    );
  };

  const handleArchive = async (id) => {
    try {
      await archiveSurveyTemplate(id);
      toast.success("Template archived");
      await load();
    } catch (e) {
      toast.error(e.message || "Could not archive template");
    }
  };

  const handleRestore = async (id) => {
    try {
      await updateSurveyTemplate(id, { is_archived: false });
      toast.success("Template restored");
      await load();
    } catch (e) {
      toast.error(e.message || "Could not restore template");
    }
  };

  const handleRename = async (id, name) => {
    try {
      await updateSurveyTemplate(id, { name });
      toast.success("Template renamed");
      await load();
    } catch (e) {
      toast.error(e.message || "Could not rename template");
    }
  };

  const active = templates.filter((t) => !t.is_archived);
  const archived = templates.filter((t) => t.is_archived);
  const shown = showArchived ? templates : active;

  return (
    <div className={embedded ? "nexus-surface-embed" : "min-h-full w-full bg-white pb-16"}>
      <Toaster position="top-center" />
      <div className={embedded ? "w-full" : "w-full px-6 py-6 max-w-5xl mx-auto"}>
        {!embedded && (
          <header className="mb-6">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-[#004f73]/70 mb-2">
              Admin · Survey
            </p>
            <h1 className="text-2xl font-bold text-gray-900">Question Templates</h1>
            <p className="text-sm text-gray-500 mt-1">
              Create and manage independent question sets. Each template has its own Full and LITE configuration.
            </p>
          </header>
        )}

        <div className="flex items-center justify-between mb-5">
          <div className="flex flex-col gap-2 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">
                Company
              </label>
              <select
                className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-white text-slate-700"
                value={scopeOrgId}
                onChange={(e) => setScopeOrgId(e.target.value)}
              >
                <option value="">Global (all companies)</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.company_name}
                  </option>
                ))}
              </select>
            </div>
            {scopeOrgId ? (
              <p className="text-xs text-slate-500">
                New invites for this company use this template. Other companies are unchanged.
              </p>
            ) : null}
            <p className="text-sm text-gray-500">
            {active.length} active template{active.length !== 1 ? "s" : ""}
            {archived.length > 0 && (
              <button
                type="button"
                onClick={() => setShowArchived((v) => !v)}
                className="ml-3 text-[#004F73] font-medium hover:underline"
              >
                {showArchived ? "Hide archived" : `Show ${archived.length} archived`}
              </button>
            )}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="px-4 py-2 text-sm font-semibold rounded-full border border-[#004F73] text-white bg-[#004F73] hover:opacity-90 shadow-sm flex items-center gap-1.5"
          >
            <span className="text-lg leading-none">+</span> New Template
          </button>
        </div>

        {loading ? (
          <p className="text-[#004F73] font-medium py-12 text-center">Loading templates…</p>
        ) : shown.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-gray-500 text-sm mb-4">No templates yet.</p>
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="px-5 py-2.5 text-sm font-semibold rounded-full text-white bg-[#004F73] hover:opacity-90"
            >
              Create your first template
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {shown.map((tpl) => (
              <TemplateCard
                key={tpl.id}
                tpl={tpl}
                onOpen={handleOpen}
                onArchive={handleArchive}
                onRestore={handleRestore}
                onRename={handleRename}
              />
            ))}
          </div>
        )}
      </div>

      <CreateTemplateModal
        open={createOpen}
        saving={creating}
        templates={templates}
        onClose={() => setCreateOpen(false)}
        onConfirm={handleCreate}
      />
    </div>
  );
}
